import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';

import type { ParseKeys } from 'i18next';

import type { AppDispatch, RootState } from '/business/shared/state/store';
import type { QuestionConfigQuestionType } from '/index';
import type { ErrorType } from '/shared/components/types';
import type {
  ElectricityHeatSteamLocationDecorated,
  ElectricityHeatSteamRenewableCertificate,
  ElectricityHeatSteamRenewableCertificateDecorated,
} from '/business/shared/state/selectors/application/electricityHeatSteam/electHeatSteamTypes';

import formatTonnage from '/shared/functions/formatTonnage';
import isNumeric from '/shared/functions/isNumeric';
import type { NumericInput } from '/shared/functions/isNumeric';

import { QUESTION_BRANCH_ACTIVITY } from '/shared/util/Statics';
import { urlApplicationRoot } from '/business/shared/util/Statics';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getElectDecoratedCertificatesSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectDecoratedCertificatesSelector';
import saveCertificate from '/business/shared/state/actions/basket/package2/saveCertificate';
import useGetWasMetaSaveSuccessful from '/business/shared/components/questions/scope2/_hooks/useGetWasMetaSaveSuccessful';
import isApplicationAnswersEditableSelector from '/business/shared/state/selectors/application/workflow/isApplicationAnswersEditableSelector';

import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import EducationWidget from '/business/shared/components/widgets/education/EducationWidget';
import Button from '/shared/components/widgets/button/Button';
import Input from '/shared/components/widgets/input/Input';
import Select from '/shared/components/widgets/select/Select';
import LinkButton from '/shared/components/widgets/button/LinkButton';
import ErrorsScroll from '/shared/components/functional/ErrorsScroll';

import StyledTextarea from '/shared/styled/components/StyledTextarea';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledRequiredFieldNote from '/shared/styled/StyledRequiredFieldNote';

import StyledElecFormRow from './components/__styled__/StyledElecFormRow';
import StyledElecFormButtons from './components/__styled__/StyledElecFormButtons';
import StyledElecFormDates from './components/__styled__/StyledElecFormDates';
import StyledAllocationSummary from './components/__styled__/StyledAllocationSummary';
import getElectNumLocationsForCertSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumLocationsForCertSelector';
import useValidateCertificateDateRange from '/business/shared/components/questions/scope2/electricityHeatSteam/useValidateCertificateDateRange';

const MIN_NUMERIC_VALUE = 0;
const MAX_NUMERIC_VALUE = 999999.99;
const TEXT_MIN_LENGTH = 3;
const TEXT_MAX_LENGTH = 3000;
const NUM_YEARS_BACK = 25;
const NUM_YEARS_FORWARD = 40;

type CertificateFormState = Omit<
  Partial<ElectricityHeatSteamRenewableCertificate>,
  | 'total_energy'
  | 'remaining_energy'
  | 'energy_allocation_for_this_carbon_review'
  | 'supplier_carbon_intensity'
  | 'date_range_start_month'
  | 'date_range_start_year'
  | 'date_range_end_month'
  | 'date_range_end_year'
> & {
  total_energy?: number | string;
  remaining_energy?: number | string;
  energy_allocation_for_this_carbon_review?: number | string;
  supplier_carbon_intensity?: number | string | null;
  date_range_start_month?: number | string;
  date_range_start_year?: number | string;
  date_range_end_month?: number | string;
  date_range_end_year?: number | string;
};

const toNumberOrNull = (value: NumericInput) => (isNumeric(value) ? Number(value) : null);

function AllocationSummary({ certificate }: { certificate?: ElectricityHeatSteamRenewableCertificateDecorated }) {
  const { t } = useTranslation();
  if (!certificate) {
    return null;
  }
  const energyTotal = certificate.energy_allocation_for_this_carbon_review_kwh || 0;
  const energyAllocated = certificate.total_kwh_used_by_locations || 0;
  const energyRemaining = energyTotal - energyAllocated;

  return (
    <div>
      <StyledAllocationSummary>
        <StyledHeading as="h3" $type="h3">
          {/* @i18n: Summary */}
          <Trans i18nKey="questions:electricityHeatSteam.certificatesAdd.summary.title" />
        </StyledHeading>
        <ul>
          <li>
            <div>{t('questions:electricityHeatSteam.certificatesAdd.summary.allocatedForPeriod')}</div>
            <div>
              {formatTonnage(energyTotal)} {t('business:emissionFactors.electricity.kwh')}
            </div>
          </li>
          <li>
            <div>{t('questions:electricityHeatSteam.certificatesAdd.summary.allocatedToLocations')}</div>
            <div>
              {formatTonnage(energyAllocated)} {t('business:emissionFactors.electricity.kwh')}
            </div>
          </li>
          <li>
            <div>{t('questions:electricityHeatSteam.certificatesAdd.summary.amountRemaining')}</div>
            <div>
              {formatTonnage(energyRemaining)} {t('business:emissionFactors.electricity.kwh')}
            </div>
          </li>
        </ul>
      </StyledAllocationSummary>
      {certificate.attachedLocations?.length > 0 && (
        <>
          <StyledHeading $type="h3" $hasExtraTopMargin>
            {/* @i18n: This energy attribute certificate is currently assigned to the following location(s): */}
            <Trans i18nKey="questions:electricityHeatSteam.certificatesAdd.summary.assignedLocationsTitle" />
          </StyledHeading>{' '}
          <StyledAllocationSummary>
            <StyledHeading as="h3" $type="h3">
              {/* @i18n: Summary */}
              <Trans i18nKey="questions:electricityHeatSteam.certificatesAdd.summary.title" />
            </StyledHeading>
            {certificate.attachedLocations.map((location: ElectricityHeatSteamLocationDecorated) => {
              const appliedFromCerts = location.attachedConsumptionData.reduce((prevRoot, consumptionDataRecord) => {
                const energyPivot = consumptionDataRecord.certificates.reduce((prevPivot, pivotEntry) => {
                  return prevPivot + (pivotEntry.applied_energy_kwh || 0);
                }, 0);
                return prevRoot + energyPivot;
              }, 0);

              const locationSpaceRatio = location.spaceRatio || 1;
              return (
                <Fragment key={`${certificate.id}-${location.id}`}>
                  <ul>
                    <li>
                      <div>{t('questions:electricityHeatSteam.certificatesAdd.summary.locationName')}</div>
                      <div>{location.unique_name}</div>
                    </li>
                    <li>
                      <div>{t('questions:electricityHeatSteam.certificatesAdd.summary.locationConsumption')}</div>
                      <div>
                        {formatTonnage((location?.aggregated_kwh || 0) * locationSpaceRatio)}{' '}
                        {t('business:emissionFactors.electricity.kwh')}
                      </div>
                    </li>
                    <li>
                      <div>{t('questions:electricityHeatSteam.certificatesAdd.summary.appliedToLocation')}</div>
                      <div>
                        {formatTonnage(appliedFromCerts)} {t('business:emissionFactors.electricity.kwh')}
                      </div>
                    </li>
                  </ul>
                </Fragment>
              );
            })}
          </StyledAllocationSummary>
        </>
      )}
    </div>
  );
}

export default function ElectricityHeatSteamCertsAdd({ question }: { question: QuestionConfigQuestionType }) {
  const { t } = useTranslation();
  const appId = useSelector(getApplicationIdSelector);
  const certificates = useSelector(getElectDecoratedCertificatesSelector);
  const wasSaveSuccessful = useGetWasMetaSaveSuccessful();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { getUrlWithAppId } = useUrlBuilder();
  const validateIsDateRangeValid = useValidateCertificateDateRange();
  const isEditable = useSelector(isApplicationAnswersEditableSelector);
  const params = new URLSearchParams(location.search);
  const editId = params.get('edit');
  const isEditMode = Boolean(editId);

  const currentCert = isEditMode ? certificates.find((cert) => cert.id?.toString() === editId) : undefined;
  const numLocationsAttached = useSelector((state: RootState) =>
    getElectNumLocationsForCertSelector(state, Number(editId)),
  );

  const monthSelectors = [...Array(12).keys()].map((key) => {
    const date = new Date(0, key);
    return {
      value: key,
      label: date.toLocaleString('en', { month: 'long' }),
    };
  });

  const currentYear = new Date().getFullYear();
  const yearSelectors = Array.from(
    { length: NUM_YEARS_BACK + NUM_YEARS_FORWARD + 1 },
    (_, i) => currentYear - NUM_YEARS_BACK + i,
  );

  const [formState, setFormState] = useState<CertificateFormState>(
    currentCert || {
      date_range_start_month: -1,
      date_range_start_year: -1,
      date_range_end_month: -1,
      date_range_end_year: -1,
      total_energy_unit: 'kwh',
      remaining_energy_unit: 'kwh',
      energy_allocation_for_this_carbon_review_unit: 'kwh',
      supplier_carbon_intensity_unit: 'kg',
    },
  );

  const [fieldErrors, setFieldErrors] = useState<Record<string, ErrorType | false>>({});

  const hasErrors = useMemo(() => Object.values(fieldErrors).some(Boolean), [fieldErrors]);
  const errors = useMemo(
    () => Array.from(Object.values(fieldErrors)).filter((error): error is ErrorType => Boolean(error)),
    [fieldErrors],
  );

  const updateFormField = <K extends keyof CertificateFormState>(
    field: K,
    value: CertificateFormState[K] | undefined,
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (fieldErrors[field as string]) {
      setFieldErrors((prev) => ({ ...prev, [field as string]: false }));
    }
  };

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, ErrorType | false> = {};
    const validationError = (code: string, key: ParseKeys): ErrorType => ({
      code,
      message: t(key),
      isTranslated: true,
    });

    const totalEnergyValue = toNumberOrNull(formState.total_energy);
    const remainingEnergyValue = toNumberOrNull(formState.remaining_energy);
    const allocationValue = toNumberOrNull(formState.energy_allocation_for_this_carbon_review);
    const supplierIntensityValue = toNumberOrNull(formState.supplier_carbon_intensity);

    const nameValue = formState.certificate_name?.trim() ?? '';
    if (nameValue.length < TEXT_MIN_LENGTH || nameValue.length > TEXT_MAX_LENGTH) {
      errors.certificate_name = validationError(
        'ERRINVALIDCERTIFICATENAME',
        'questions:electricityHeatSteam.certificatesAdd.validation.certificateName',
      );
    }

    const certificateIdValue = formState.certificate_id?.trim() ?? '';
    if (
      certificateIdValue &&
      (certificateIdValue.length < TEXT_MIN_LENGTH || certificateIdValue.length > TEXT_MAX_LENGTH)
    ) {
      errors.certificate_id = validationError(
        'ERRINVALIDCERTIFICATEID',
        'questions:electricityHeatSteam.certificatesAdd.validation.certificateId',
      );
    }

    if (totalEnergyValue === null || totalEnergyValue < MIN_NUMERIC_VALUE || totalEnergyValue > MAX_NUMERIC_VALUE) {
      errors.total_energy = validationError(
        'ERRTOTALENERGYOUTOFRANGE',
        'questions:electricityHeatSteam.certificatesAdd.validation.totalEnergy',
      );
    }
    // Not needed if a valid unit is always preselected
    // else if (!ALLOWED_ENERGY_UNITS.includes(formState.total_energy_unit ?? '')) {
    //   errors.total_energy = {
    //     code: 'ERRTOTALENERGYUNIT',
    //     message: 'Select a valid energy unit',
    //   };
    // }

    if (
      remainingEnergyValue === null ||
      remainingEnergyValue < MIN_NUMERIC_VALUE ||
      remainingEnergyValue > MAX_NUMERIC_VALUE
    ) {
      errors.remaining_energy = validationError(
        'ERRREMAININGENERGYOUTOFRANGE',
        'questions:electricityHeatSteam.certificatesAdd.validation.remainingEnergy',
      );
    }
    // Not needed if a valid unit is always preselected
    // else if (!ALLOWED_ENERGY_UNITS.includes(formState.remaining_energy_unit ?? '')) {
    //   errors.remaining_energy = {
    //     code: 'ERRREMAININGENERGYUNIT',
    //     message: 'Select a valid energy unit',
    //   };
    // }

    if (allocationValue === null || allocationValue < MIN_NUMERIC_VALUE || allocationValue > MAX_NUMERIC_VALUE) {
      errors.energy_allocation_for_this_carbon_review = validationError(
        'ERRENERGYALLOCATIONOUTOFRANGE',
        'questions:electricityHeatSteam.certificatesAdd.validation.allocation',
      );
    }
    // Not needed if a valid unit is always preselected
    // else if (!ALLOWED_ENERGY_UNITS.includes(formState.energy_allocation_for_this_carbon_review_unit ?? '')) {
    //   errors.energy_allocation_for_this_carbon_review = {
    //     code: 'ERRENERGYALLOCATIONUNIT',
    //     message: 'Select a valid energy unit',
    //   };
    // }

    if (
      !errors.remaining_energy &&
      totalEnergyValue !== null &&
      remainingEnergyValue !== null &&
      totalEnergyValue < remainingEnergyValue
    ) {
      errors.remaining_energy = validationError(
        'ERRREMAININGENERGYTOOHIGH',
        'questions:electricityHeatSteam.certificatesAdd.validation.remainingTooHigh',
      );
    }

    if (
      !errors.energy_allocation_for_this_carbon_review &&
      remainingEnergyValue !== null &&
      allocationValue !== null &&
      remainingEnergyValue < allocationValue
    ) {
      errors.energy_allocation_for_this_carbon_review = validationError(
        'ERRENERGYALLOCATIONTOOHIGH',
        'questions:electricityHeatSteam.certificatesAdd.validation.allocationTooHigh',
      );
    }

    if (
      supplierIntensityValue === null ||
      supplierIntensityValue < MIN_NUMERIC_VALUE ||
      supplierIntensityValue > MAX_NUMERIC_VALUE
    ) {
      errors.supplier_carbon_intensity = validationError(
        'ERRSUPPLIERINTENSITYRANGE',
        'questions:electricityHeatSteam.certificatesAdd.validation.supplierIntensity',
      );
    }

    // Not needed if a valid unit is always preselected
    // if (
    //   !errors.supplier_carbon_intensity &&
    //   formState.supplier_carbon_intensity_unit &&
    //   !ALLOWED_INTENSITY_UNITS.includes(formState.supplier_carbon_intensity_unit)
    // ) {
    //   errors.supplier_carbon_intensity = {
    //     code: 'ERRSUPPLIERINTENSITYUNIT',
    //     message: 'Select a valid intensity unit',
    //   };
    // }

    const startMonthValue = toNumberOrNull(formState.date_range_start_month);
    const startYearValue = toNumberOrNull(formState.date_range_start_year);
    const endMonthValue = toNumberOrNull(formState.date_range_end_month);
    const endYearValue = toNumberOrNull(formState.date_range_end_year);

    // Not needed if a valid unit is always preselected
    if (startMonthValue === -1) {
      errors.date_range_start_month = validationError(
        'ERRSTARTMONTH',
        'questions:electricityHeatSteam.certificatesAdd.validation.startMonth',
      );
    }

    if (startYearValue === -1) {
      errors.date_range_start_year = validationError(
        'ERRSTARTYEAR',
        'questions:electricityHeatSteam.certificatesAdd.validation.startYear',
      );
    }

    if (endMonthValue === -1) {
      errors.date_range_end_month = validationError(
        'ERRENDMONTH',
        'questions:electricityHeatSteam.certificatesAdd.validation.endMonth',
      );
    }

    // Not needed if a valid unit is always preselected
    if (endYearValue === -1) {
      errors.date_range_end_year = validationError(
        'ERRENDYEAR',
        'questions:electricityHeatSteam.certificatesAdd.validation.endYear',
      );
    }

    if (startMonthValue !== null && startYearValue !== null && endMonthValue !== null && endYearValue !== null) {
      const startDate = new Date(startYearValue, startMonthValue, 1);
      const endDate = new Date(endYearValue, endMonthValue, 1);

      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        errors.date_range = validationError(
          'ERRINVALIDDATERANGE',
          'questions:electricityHeatSteam.certificatesAdd.validation.dateRange',
        );
      } else if (startDate.getTime() > endDate.getTime()) {
        //   if (startDate.getTime() > endDate.getTime()) {
        errors.date_range = validationError(
          'ERRINVALIDDATERANGE',
          'questions:electricityHeatSteam.certificatesAdd.validation.endDateAfterStart',
        );
      }

      if (numLocationsAttached > 0) {
        // in use, so ensure date range still valid
        if (!validateIsDateRangeValid(null, startDate, endDate)) {
          errors.date_range = validationError(
            'ERRINVALIDDATERANGE',
            'questions:electricityHeatSteam.certificatesAdd.validation.inUseDateRange',
          );
        }
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formState, numLocationsAttached, validateIsDateRangeValid, t]);

  // Convert form state to certificate type for saving
  const formStateToCertificate = (): ElectricityHeatSteamRenewableCertificate => ({
    id: currentCert?.id || undefined,
    certificate_name: formState.certificate_name || '',
    certificate_id: formState.certificate_id || '',
    description: formState.description || '',
    total_energy: toNumberOrNull(formState.total_energy) ?? 0,
    total_energy_unit: formState.total_energy_unit || 'kwh',
    remaining_energy: toNumberOrNull(formState.remaining_energy) ?? 0,
    remaining_energy_unit: formState.remaining_energy_unit || 'kwh',
    energy_allocation_for_this_carbon_review: toNumberOrNull(formState.energy_allocation_for_this_carbon_review) ?? 0,
    energy_allocation_for_this_carbon_review_unit: formState.energy_allocation_for_this_carbon_review_unit || 'kwh',
    supplier_carbon_intensity: toNumberOrNull(formState.supplier_carbon_intensity),
    supplier_carbon_intensity_unit: formState.supplier_carbon_intensity_unit || 'kg',
    date_range_start_month: toNumberOrNull(formState.date_range_start_month) ?? 0,
    date_range_start_year: toNumberOrNull(formState.date_range_start_year) ?? currentYear - 1,
    date_range_end_month: toNumberOrNull(formState.date_range_end_month) ?? 0,
    date_range_end_year: toNumberOrNull(formState.date_range_end_year) ?? currentYear,
  });

  const listRoute = getUrlWithAppId(
    `/${urlApplicationRoot}/questions/calculation/scope-2/electricity-heat-steam/energy-attribute-certificates`,
    appId,
  );

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const certificate = formStateToCertificate();

    // @todo: Typescript for redux
    await dispatch(
      saveCertificate({
        certificate,
        applicationId: appId,
      }),
    );
  };
  useEffect(() => {
    if (wasSaveSuccessful) {
      navigate(listRoute);
    }
  }, [wasSaveSuccessful, listRoute, navigate]);

  if (isEditMode && !currentCert) {
    return <div>{t('questions:electricityHeatSteam.certificatesAdd.certificateNotFound')}</div>;
  }

  return (
    <>
      <EducationWidget question={question} branchType={QUESTION_BRANCH_ACTIVITY} />

      <div>
        <StyledHeading as="h1" $type="h1">
          {isEditMode ? (
            <>
              {/* @i18n: Electricity, heat and steam - Edit energy attribute certificate */}
              <Trans i18nKey="questions:electricityHeatSteam.certificatesAdd.editTitle" />
            </>
          ) : (
            <>
              {/* @i18n: Electricity, heat and steam - Add new energy attribute certificates */}
              <Trans i18nKey="questions:electricityHeatSteam.certificatesAdd.addTitle" />
            </>
          )}
        </StyledHeading>
        <StyledParagraph $largeText>
          {/* @i18n: If your business has valid energy attribute certificates for the reporting period[...] */}
          <Trans
            i18nKey="questions:electricityHeatSteam.certificates.intro"
            components={{ 1: <ReportingPeriod $green /> }}
          />
        </StyledParagraph>
        <StyledParagraph $smallText $hasExtraMarginBottom>
          {/* @i18n: e.g. Power Purchase Agreements (PPA), Renewable Energy Guarantee of Origin (REGO), or[...] */}
          <Trans i18nKey="questions:electricityHeatSteam.certificates.example" />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>
            {/* @i18n: For more information, refer to the "Help" section for detailed guidance. */}
            <Trans i18nKey="questions:electricityHeatSteam.shared.helpText" />
          </div>
        </StyledInfoFlash>
        {hasErrors && <ErrorsScroll errors={errors} hasErrors={hasErrors} />}
      </div>
      <div>
        <StyledHeading as="h2" $type="h2">
          {/* @i18n: Energy attribute certificate details */}
          <Trans i18nKey="questions:electricityHeatSteam.certificatesAdd.detailsTitle" />
        </StyledHeading>
        <form onSubmit={handleSave}>
          <StyledElecFormRow>
            <label htmlFor="certificateName">
              {t('questions:electricityHeatSteam.certificatesAdd.labels.certificateName')}
            </label>
            <Input
              id="certificateName"
              type="text"
              onChange={(event) => updateFormField('certificate_name', event.target.value)}
              minLength={TEXT_MIN_LENGTH}
              maxLength={TEXT_MAX_LENGTH}
              value={formState.certificate_name}
              placeholder={t('questions:electricityHeatSteam.certificatesAdd.placeholders.certificateName')}
              $isErrored={Boolean(fieldErrors.certificate_name)}
            />
          </StyledElecFormRow>

          <StyledElecFormRow>
            <label htmlFor="certificateId">
              {t('questions:electricityHeatSteam.certificatesAdd.labels.certificateId')}
            </label>
            <Input
              id="certificateId"
              type="text"
              onChange={(event) => updateFormField('certificate_id', event.target.value)}
              minLength={TEXT_MIN_LENGTH}
              maxLength={TEXT_MAX_LENGTH}
              value={formState.certificate_id}
              placeholder={t('questions:electricityHeatSteam.certificatesAdd.placeholders.certificateId')}
              $isErrored={Boolean(fieldErrors.certificate_id)}
            />
          </StyledElecFormRow>

          <StyledElecFormRow>
            <label htmlFor="description">
              {t('questions:electricityHeatSteam.certificatesAdd.labels.description')}
              <br />
              <small>{t('questions:electricityHeatSteam.certificatesAdd.optional')}</small>
            </label>
            <StyledTextarea
              id="description"
              onChange={(event) => updateFormField('description', event.target.value)}
              value={formState.description}
              placeholder=""
              rows={3}
            />
          </StyledElecFormRow>

          <StyledElecFormRow>
            <label htmlFor="totalEnergy">
              {t('questions:electricityHeatSteam.certificatesAdd.labels.totalEnergy')}
            </label>
            <Input
              id="totalEnergy"
              type="number"
              inputMode="decimal"
              step="0.000001"
              min={MIN_NUMERIC_VALUE}
              max={MAX_NUMERIC_VALUE}
              onChange={(event) => updateFormField('total_energy', event.target.value)}
              value={formState.total_energy ?? ''}
              placeholder="0.00"
              $isErrored={Boolean(fieldErrors.total_energy)}
              dropdownItems={[
                { name: 'kwh', displayName: 'kWh' },
                { name: 'mwh', displayName: 'MWh' },
              ]}
              dropdownValue={formState.total_energy_unit}
              onDropdownChange={(event) =>
                updateFormField('total_energy_unit', (event.target.value as 'kwh' | 'mwh') || 'kwh')
              }
            />
          </StyledElecFormRow>

          <StyledElecFormRow>
            <label htmlFor="remainingEnergy">
              {t('questions:electricityHeatSteam.certificatesAdd.labels.remainingEnergy')}
            </label>
            <Input
              id="remainingEnergy"
              type="number"
              inputMode="decimal"
              step="0.000001"
              min={MIN_NUMERIC_VALUE}
              max={MAX_NUMERIC_VALUE}
              onChange={(event) => updateFormField('remaining_energy', event.target.value)}
              value={formState.remaining_energy ?? ''}
              placeholder="0.00"
              $isErrored={Boolean(fieldErrors.remaining_energy)}
              dropdownItems={[
                { name: 'kwh', displayName: 'kWh' },
                { name: 'mwh', displayName: 'MWh' },
              ]}
              dropdownValue={formState.remaining_energy_unit}
              onDropdownChange={(event) =>
                updateFormField('remaining_energy_unit', (event.target.value as 'kwh' | 'mwh') || 'kwh')
              }
            />
          </StyledElecFormRow>

          <StyledElecFormRow>
            <label htmlFor="allocationEnergy">
              {t('questions:electricityHeatSteam.certificatesAdd.labels.allocation')}
            </label>
            <Input
              id="allocationEnergy"
              type="number"
              inputMode="decimal"
              step="0.000001"
              min={MIN_NUMERIC_VALUE}
              max={MAX_NUMERIC_VALUE}
              onChange={(event) => updateFormField('energy_allocation_for_this_carbon_review', event.target.value)}
              value={formState.energy_allocation_for_this_carbon_review ?? ''}
              placeholder="0.00"
              $isErrored={Boolean(fieldErrors.energy_allocation_for_this_carbon_review)}
              dropdownItems={[
                { name: 'kwh', displayName: 'kWh' },
                { name: 'mwh', displayName: 'MWh' },
              ]}
              dropdownValue={formState.energy_allocation_for_this_carbon_review_unit}
              onDropdownChange={(event) =>
                updateFormField(
                  'energy_allocation_for_this_carbon_review_unit',
                  (event.target.value as 'kwh' | 'mwh') || 'kwh',
                )
              }
            />
          </StyledElecFormRow>

          <StyledElecFormRow>
            <label htmlFor="supplierCarbonIntensity">
              {t('questions:electricityHeatSteam.certificatesAdd.labels.supplierIntensity')}*
            </label>
            <Input
              id="supplierCarbonIntensity"
              type="number"
              inputMode="decimal"
              step="0.000001"
              min={MIN_NUMERIC_VALUE}
              max={MAX_NUMERIC_VALUE}
              value={formState.supplier_carbon_intensity ?? ''}
              placeholder="0.00"
              $isErrored={Boolean(fieldErrors.supplier_carbon_intensity)}
              onChange={(event) => updateFormField('supplier_carbon_intensity', event.target.value)}
              dropdownItems={[
                { name: 'kg', displayName: 'kgCO₂e/Unit' },
                { name: 'tonne', displayName: 'tCO₂e/Unit' },
              ]}
              dropdownValue={formState.supplier_carbon_intensity_unit}
              onDropdownChange={(event) =>
                updateFormField('supplier_carbon_intensity_unit', (event.target.value as 'kg' | 'tonne') || 'kg')
              }
            />
          </StyledElecFormRow>

          <StyledElecFormRow>
            <label htmlFor="startMonth">{t('questions:electricityHeatSteam.certificatesAdd.labels.dateRange')}</label>
            <StyledElecFormDates>
              <div>
                <Select
                  id="startMonth"
                  value={formState.date_range_start_month}
                  onChange={(event) => updateFormField('date_range_start_month', event.target.value)}
                  $isErrored={Boolean(
                    fieldErrors.date_range_start_month || fieldErrors.date_range_start_year || fieldErrors.date_range,
                  )}
                >
                  <option value={-1}>
                    {t('questions:electricityHeatSteam.certificatesAdd.placeholders.startMonth')}
                  </option>
                  {monthSelectors.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
                <Select
                  value={formState.date_range_start_year}
                  onChange={(event) => updateFormField('date_range_start_year', event.target.value)}
                  $isErrored={Boolean(
                    fieldErrors.date_range_start_month || fieldErrors.date_range_start_year || fieldErrors.date_range,
                  )}
                >
                  <option value={-1}>
                    {t('questions:electricityHeatSteam.certificatesAdd.placeholders.startYear')}
                  </option>

                  {yearSelectors.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Select
                  value={formState.date_range_end_month}
                  onChange={(event) => updateFormField('date_range_end_month', event.target.value)}
                  $isErrored={Boolean(
                    fieldErrors.date_range_end_month || fieldErrors.date_range_end_year || fieldErrors.date_range,
                  )}
                >
                  <option value={-1}>
                    {t('questions:electricityHeatSteam.certificatesAdd.placeholders.endMonth')}
                  </option>
                  {monthSelectors.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
                <Select
                  value={formState.date_range_end_year}
                  onChange={(event) => updateFormField('date_range_end_year', event.target.value)}
                  $isErrored={Boolean(
                    fieldErrors.date_range_end_month || fieldErrors.date_range_end_year || fieldErrors.date_range,
                  )}
                >
                  <option value={-1}>{t('questions:electricityHeatSteam.certificatesAdd.placeholders.endYear')}</option>
                  {yearSelectors.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Select>
              </div>
            </StyledElecFormDates>
          </StyledElecFormRow>

          {isEditMode && <AllocationSummary certificate={currentCert} />}

          <StyledElecFormButtons>
            <Button type="submit" $primary disabled={!isEditable}>
              {t(isEditMode ? 'business:Buttons.update' : 'business:Buttons.save')}
            </Button>
            <LinkButton $secondary to={listRoute}>
              {/* @i18n: Cancel and return to Energy attribute certificates */}
              <Trans i18nKey="questions:electricityHeatSteam.certificatesAdd.cancelReturn" />
            </LinkButton>
          </StyledElecFormButtons>

          <StyledRequiredFieldNote style={{ marginTop: '-2.5rem' }}>
            {/* @i18n: * Consider the amount of renewable energy already retired and the expiration date of[...] */}
            <Trans i18nKey="questions:electricityHeatSteam.certificatesAdd.requiredNote" />
          </StyledRequiredFieldNote>
        </form>
      </div>
    </>
  );
}
